import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePersistedFilterState } from '@/hooks/usePersistedFilterState';
import { FilterRule, FilterField, FilterGroup, FilterEntity, FilterAttribute } from '@/types/filters';
import { Estimate } from './useEstimateData';
import { supabase } from '@/integrations/supabase/client';
import { getEffectiveEstimateStatus } from '@/utils/estimateStatus';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';

type Option = { value: string; label: string };

export const useEstimateFilterBuilder = (estimates: Estimate[]) => {
  const [draftGroups, setDraftGroups] = usePersistedFilterState<FilterGroup[]>('filters-estimates-draft', []);
  const [appliedGroups, setAppliedGroups] = usePersistedFilterState<FilterGroup[]>('filters-estimates-applied', []);

  const [optionsOverride, setOptionsOverride] = useState<{
    customers: Option[];
    projects: Option[];
    leadSources: Option[];
    leadOwners: Option[];
    createdBy: Option[];
    cities: Option[];
    provinces: Option[];
    suburbs: Option[];
    tags: Option[];
    projectStatuses: Option[];
  }>({ 
    customers: [], 
    projects: [], 
    leadSources: [], 
    leadOwners: [], 
    createdBy: [],
    cities: [], 
    provinces: [], 
    suburbs: [], 
    tags: [],
    projectStatuses: []
  });

  // Fetch comprehensive filter options from database
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch customers, projects, and profiles in parallel
        const [customersRes, projectsRes, profilesRes] = await Promise.all([
          supabase.from('customers').select('id, name, last_name, customer_type, business_name, representative_first_name, representative_last_name, lead_source, suburb, city, province_state, tags, lead_owner_id'),
          supabase.from('projects').select('id, name, status'),
          supabase.from('profiles').select('id, first_name, last_name')
        ]);

        if (customersRes.error) throw customersRes.error;
        if (projectsRes.error) throw projectsRes.error;
        if (profilesRes.error) throw profilesRes.error;

        // Process customers data
        const leadSourcesSet = new Set<string>();
        const suburbsSet = new Set<string>();
        const citiesSet = new Set<string>();
        const provincesSet = new Set<string>();
        const tagsSet = new Set<string>();
        const ownerIdsSet = new Set<string>();

        (customersRes.data || []).forEach((c: any) => {
          if (c.lead_source) leadSourcesSet.add(c.lead_source);
          if (c.suburb) suburbsSet.add(c.suburb);
          if (c.city) citiesSet.add(c.city);
          if (c.province_state) provincesSet.add(c.province_state);
          if (Array.isArray(c.tags)) c.tags.filter(Boolean).forEach((t: string) => tagsSet.add(t));
          if (c.lead_owner_id) ownerIdsSet.add(c.lead_owner_id);
        });

        // Create options and sort alphabetically
        const customerOptions = (customersRes.data || []).map((c: any) => ({
          value: c.id,
          label: getCustomerDisplayName(c)
        })).sort((a, b) => a.label.localeCompare(b.label));

        const projectOptions = (projectsRes.data || []).map((p: any) => ({
          value: p.id,
          label: p.name
        })).sort((a, b) => a.label.localeCompare(b.label));

        const projectStatuses = [...new Set((projectsRes.data || []).map((p: any) => p.status).filter(Boolean))].sort();

        // Create profile options for lead owners and created by
        const profileOptions = (profilesRes.data || []).map((p: any) => ({
          value: p.id,
          label: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.id
        })).sort((a, b) => a.label.localeCompare(b.label));

        const leadOwnerOptions = profileOptions.filter(p => ownerIdsSet.has(p.value));

        setOptionsOverride({
          customers: customerOptions,
          projects: projectOptions,
          leadSources: Array.from(leadSourcesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          leadOwners: leadOwnerOptions,
          createdBy: profileOptions,
          cities: Array.from(citiesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          provinces: Array.from(provincesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          suburbs: Array.from(suburbsSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          tags: Array.from(tagsSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          projectStatuses: projectStatuses.map(s => ({ value: s, label: s }))
        });
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };

    fetchOptions();
  }, []);

  // Get unique values from current estimates for fallback options
  const dynamicOptions = useMemo(() => {
    const leadSources = [...new Set(
      estimates
        .map(e => e.customers?.lead_source)
        .filter(Boolean)
    )].map(source => ({ value: source!, label: source! })).sort((a, b) => a.label.localeCompare(b.label));

    const customers = [...new Set(
      estimates
        .map(e => e.customers ? getCustomerDisplayName(e.customers) : null)
        .filter(Boolean)
    )].map(name => ({ value: name!, label: name! })).sort((a, b) => a.label.localeCompare(b.label));

    const projects = [...new Set(
      estimates
        .map(e => e.projects?.name)
        .filter(Boolean)
    )].map(name => ({ value: name!, label: name! })).sort((a, b) => a.label.localeCompare(b.label));

    const createdBy = [...new Set(
      estimates
        .map(e => e.profiles ? `${e.profiles.first_name || ''} ${e.profiles.last_name || ''}`.trim() : null)
        .filter(Boolean)
    )].map(name => ({ value: name!, label: name! })).sort((a, b) => a.label.localeCompare(b.label));

    return {
      leadSources: optionsOverride.leadSources.length ? optionsOverride.leadSources : leadSources,
      customers: optionsOverride.customers.length ? optionsOverride.customers : customers,
      projects: optionsOverride.projects.length ? optionsOverride.projects : projects,
      createdBy: optionsOverride.createdBy.length ? optionsOverride.createdBy : createdBy,
      cities: optionsOverride.cities,
      provinces: optionsOverride.provinces,
      suburbs: optionsOverride.suburbs,
      tags: optionsOverride.tags,
      projectStatuses: optionsOverride.projectStatuses,
    };
  }, [estimates, optionsOverride]);

  // Filter configuration - 5 main entities
  const filterEntities: FilterEntity[] = [
    { key: 'estimate', label: 'Estimate' },
    { key: 'customer', label: 'Customer' },
    { key: 'project', label: 'Project' },
    { key: 'financial', label: 'Financial' },
    { key: 'timeline', label: 'Timeline' }
  ];

  // Attributes per entity
  const filterAttributesByEntity: Record<string, FilterAttribute[]> = {
    estimate: [
      { key: 'status', label: "'s Status", type: 'select', options: [
        { value: 'draft', label: 'Draft' },
        { value: 'sent', label: 'Sent' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'expired', label: 'Expired' }
      ]},
      { key: 'version', label: " 's Version", type: 'number' },
      { key: 'estimate_number', label: "'s Number", type: 'text' },
      { key: 'title', label: "'s Title", type: 'text' },
      { key: 'description', label: "'s Description", type: 'text' },
      { key: 'payment_terms', label: "'s Payment Terms", type: 'text' },
      { key: 'special_instructions', label: "'s Special Instructions", type: 'text' },
      { key: 'validity_period', label: "'s Validity Period (days)", type: 'number' },
      { key: 'created_by', label: "'s Created By", type: 'select', options: dynamicOptions.createdBy },
      { key: 'has_versions', label: 'Has Multiple Versions', type: 'boolean' },
      { key: 'version_count', label: "'s Number of Versions", type: 'number' }
    ],
    customer: [
      { key: 'customer_type', label: "'s Type", type: 'select', options: [
        { value: 'residential', label: 'Residential' },
        { value: 'commercial', label: 'Commercial' }
      ]},
      { key: 'customer_name', label: "'s Name", type: 'select', options: dynamicOptions.customers },
      { key: 'lead_source', label: "'s Lead Source", type: 'select', options: dynamicOptions.leadSources },
      { key: 'lead_owner', label: "'s Lead Owner", type: 'select', options: dynamicOptions.createdBy },
      { key: 'suburb', label: "'s Suburb", type: 'select', options: dynamicOptions.suburbs },
      { key: 'city', label: "'s City", type: 'select', options: dynamicOptions.cities },
      { key: 'province_state', label: "'s Province/State", type: 'select', options: dynamicOptions.provinces },
      { key: 'tags', label: "'s Tags", type: 'select', options: dynamicOptions.tags },
      { key: 'lead_age_days', label: "'s Lead Age (days)", type: 'number' }
    ],
    project: [
      { key: 'project_name', label: "'s Name", type: 'select', options: dynamicOptions.projects },
      { key: 'project_status', label: "'s Status", type: 'select', options: dynamicOptions.projectStatuses },
      { key: 'has_project', label: 'Exists', type: 'boolean' }
    ],
    financial: [
      { key: 'total_amount', label: 'Total Amount', type: 'number' },
      { key: 'tax_inclusive', label: 'Tax Inclusive', type: 'boolean' },
      { key: 'tax_percentage', label: 'Tax Percentage', type: 'number' },
      { key: 'has_invoices', label: 'Has Invoices', type: 'boolean' },
      { key: 'invoice_count', label: 'Number of Invoices', type: 'number' },
      { key: 'invoice_paid_count', label: 'Number of Paid Invoices', type: 'number' },
      { key: 'invoice_sent_count', label: 'Number of Sent Invoices', type: 'number' },
      { key: 'invoice_cancelled_count', label: 'Number of Cancelled Invoices', type: 'number' },
      { key: 'invoicing_status', label: 'Invoicing Status', type: 'select', options: [
        { value: 'not_started', label: 'Not Started' },
        { value: 'partial', label: 'Partial' },
        { value: 'fully_invoiced', label: 'Fully Invoiced' }
      ]},
      { key: 'total_invoiced', label: 'Total Invoiced', type: 'number' }
    ],
    timeline: [
      { key: 'created_at', label: 'Created Date', type: 'date' },
      { key: 'updated_at', label: 'Updated Date', type: 'date' },
      { key: 'sent_date', label: 'Sent Date', type: 'date' },
      { key: 'accepted_date', label: 'Accepted Date', type: 'date' },
      { key: 'rejected_date', label: 'Rejected Date', type: 'date' },
      { key: 'expired_date', label: 'Expired Date', type: 'date' },
      { key: 'expires_at', label: 'Expires At', type: 'date' }
    ]
  };

  // Generate filter fields for UI
  const filterFields = useMemo(() => {
    const fields: FilterField[] = [];
    filterEntities.forEach(entity => {
      const attributes = filterAttributesByEntity[entity.key] || [];
      attributes.forEach(attr => {
        fields.push({
          key: `${entity.key}.${attr.key}`,
          label: `${entity.label} - ${attr.label}`,
          type: attr.type,
          options: attr.options
        });
      });
    });
    return fields;
  }, [dynamicOptions]);

  // Filter group management functions
  const addGroup = useCallback(() => {
    const newRule: FilterRule = {
      id: Date.now().toString(),
      entity: '',
      attribute: '',
      operator: 'equals',
      value: '',
      logic: 'AND'
    };

    const newGroup: FilterGroup = {
      id: Date.now().toString(),
      rules: [newRule], // Start with one empty rule
      logic: 'AND'
    };
    setDraftGroups(prev => [...prev, newGroup]);
  }, []);

  const updateGroup = useCallback((groupId: string, updates: Partial<FilterGroup>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId ? { ...group, ...updates } : group
    ));
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setDraftGroups(prev => prev.filter(group => group.id !== groupId));
  }, []);

  const addRuleToGroup = useCallback((groupId: string) => {
    const newRule: FilterRule = {
      id: Date.now().toString(),
      entity: '',
      attribute: '',
      operator: 'equals',
      value: '',
      logic: 'AND'
    };
    
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, rules: [...group.rules, newRule] }
        : group
    ));
  }, []);

  const updateRuleInGroup = useCallback((groupId: string, ruleId: string, updates: Partial<FilterRule>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? {
            ...group,
            rules: group.rules.map(rule => 
              rule.id === ruleId ? { ...rule, ...updates } : rule
            )
          }
        : group
    ));
  }, []);

  const removeRuleFromGroup = useCallback((groupId: string, ruleId: string) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, rules: group.rules.filter(rule => rule.id !== ruleId) }
        : group
    ));
  }, []);

  const clearAllRules = useCallback(() => {
    setDraftGroups([]);
    setAppliedGroups([]);
  }, []);

  // Check if a rule is complete
  const isRuleComplete = useCallback((rule: FilterRule): boolean => {
    if (!rule.entity || !rule.attribute || !rule.operator) return false;
    
    if (rule.operator === 'is_empty' || rule.operator === 'is_not_empty') {
      return true;
    }
    
    if (rule.operator === 'between') {
      return Array.isArray(rule.value) && rule.value.length === 2 && 
             rule.value[0] !== '' && rule.value[1] !== '';
    }
    
    if (rule.operator === 'in' || rule.operator === 'not_in') {
      return Array.isArray(rule.value) && rule.value.length > 0;
    }
    
    return rule.value !== '' && rule.value !== null && rule.value !== undefined;
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    setAppliedGroups([...draftGroups]);
  }, [draftGroups]);

  const hasActiveFilters = appliedGroups.length > 0;
  const hasUnappliedChanges = JSON.stringify(draftGroups) !== JSON.stringify(appliedGroups);
  const canApplyFilters = draftGroups.some(group => 
    group.rules.some(rule => isRuleComplete(rule))
  );

  // Get field value from estimate
  const getFieldValue = useCallback((estimate: Estimate, entity: string, attribute: string): any => {
    switch (entity) {
      case 'estimate':
        switch (attribute) {
          case 'status': 
            // CRITICAL: Use the same status computation logic as the display
            return getEffectiveEstimateStatus(estimate.status as any, estimate.expires_at);
          case 'created_by': 
            return estimate.profiles ? `${estimate.profiles.first_name || ''} ${estimate.profiles.last_name || ''}`.trim() : '';
          default: 
            return estimate[attribute as keyof Estimate];
        }
      case 'customer':
        if (!estimate.customers) return null;
        switch (attribute) {
          case 'customer_type': return estimate.customers.customer_type;
          case 'customer_name': return getCustomerDisplayName(estimate.customers);
          case 'lead_owner': return estimate.customers.lead_owner_id;
          case 'lead_age_days': return estimate.lead_age_days;
          default: return estimate.customers[attribute as keyof typeof estimate.customers];
        }
      case 'project':
        if (!estimate.projects) return null;
        switch (attribute) {
          case 'project_name': return estimate.projects.name;
          case 'project_status': return estimate.projects.status;
          default: return estimate[attribute as keyof Estimate];
        }
      case 'financial':
        return estimate[attribute as keyof Estimate];
      case 'timeline':
        return estimate[attribute as keyof Estimate];
      default:
        return null;
    }
  }, []);

  // Evaluate a single rule
  const evaluateRule = useCallback((estimate: Estimate, rule: FilterRule): boolean => {
    if (!isRuleComplete(rule)) return true;

    const fieldValue = getFieldValue(estimate, rule.entity, rule.attribute);
    const { operator, value } = rule;

    // Handle null/undefined values
    if (fieldValue === null || fieldValue === undefined) {
      return operator === 'is_empty' || operator === 'not_equals';
    }

    switch (operator) {
      case 'equals':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return fieldValue.some(v => value.includes(v));
          }
          return value.includes(fieldValue);
        }
        return fieldValue == value;
      case 'not_equals':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return !fieldValue.some(v => value.includes(v));
          }
          return !value.includes(fieldValue);
        }
        return fieldValue != value;
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      case 'not_contains':
        return !String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      case 'greater_than':
        return Number(fieldValue) > Number(value);
      case 'less_than':
        return Number(fieldValue) < Number(value);
      case 'between':
        if (Array.isArray(value) && value.length === 2) {
          const num = Number(fieldValue);
          return num >= Number(value[0]) && num <= Number(value[1]);
        }
        return false;
      case 'in':
        return Array.isArray(value) && value.includes(fieldValue);
      case 'not_in':
        return Array.isArray(value) && !value.includes(fieldValue);
      case 'is_empty':
        return !fieldValue || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0);
      case 'is_not_empty':
        return fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0);
      default:
        return true;
    }
  }, [getFieldValue, isRuleComplete]);

  // Filter estimates based on applied groups
  const filteredEstimates = useMemo(() => {
    if (appliedGroups.length === 0) return estimates;

    return estimates.filter(estimate => {
      return appliedGroups.every(group => {
        if (group.rules.length === 0) return true;

        const completeRules = group.rules.filter(rule => isRuleComplete(rule));
        if (completeRules.length === 0) return true;

        if (group.logic === 'OR') {
          return completeRules.some(rule => evaluateRule(estimate, rule));
        } else {
          return completeRules.every(rule => evaluateRule(estimate, rule));
        }
      });
    });
  }, [estimates, appliedGroups, evaluateRule, isRuleComplete]);

  return {
    // State
    draftGroups,
    appliedGroups,
    
    // Data
    filteredEstimates,
    
    // Computed flags
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
    
    // Actions
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    applyFilters,
    clearAllRules,
    
    // Configuration
    filterFields,
    filterEntities,
    filterAttributesByEntity,
  };
};
